import {
  DeleteIcon,
  MapSettingsIcon,
  SortDownIcons,
  SortUpIcons,
} from '@/assets/icons/icons';
import {
  CustomRadioGroup,
  CustomTitle,
  TeamBadge,
  TruncateWithTooltip,
} from '@/components';
import { Flex, Input, Popover, Table, Tooltip } from 'antd';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HeaderItem, ShipmentItem, TimeSetting } from '../types';
import TableSectionEmptyState from './TableSectionEmptyState';
// import VirtualList from '@/pages/Shipments/partials/VirtualList';
// const VIRTUAL_TABLE_HEIGHT = 480;
// const ROW_HEIGHT = 44;
// const DEFAULT_COLUMN_WIDTH = 165;
// const CELL_PADDING = '8px 4px';
import theme from '@/assets/theme';
import { parseShipmentDayjs } from '@/common/utils/utils';

type SortOrder = 'asc' | 'desc' | null;
type TimezoneValue =
  'local_scan_time' | 'origin' | 'destination' | 'user_timezone';
type CompleteHeaderItem = HeaderItem & { key: string; value: string };

export interface teams {
  color: string;
  id: string;
  name: string;
}

interface DownloadReportTableSectionProps {
  hasMore?: boolean;
  columnTimeSettings: Record<string, TimeSetting>;
  onTimeSettingsChange: (
    columnKey: string,
    patch: Partial<{ timezone: TimezoneValue; format: string }>
  ) => void;
  headerList: HeaderItem[];
  shipmentData: ShipmentItem[];
  // sheetTitle: string;
  // setSheetTitle: React.Dispatch<React.SetStateAction<string>>;
  // onAddEmptyColumn: () => void;
  onDeleteCustomColumn: (columnKey: string) => void;
  onCustomCellChange: (
    rowIndex: number,
    columnKey: string,
    value: string
  ) => void;
  onHeaderUpdate?: (oldKey: string, newKey: string, newValue: string) => void;
  handleLoadMore: () => void;
  loading: boolean;
  // calculationStatus?: string;
  // handleRecalculate: () => void;
  // lastRefreshFormattedTime?: string;

  searchQuery?: string;
  onFoundMatches?: (matches: HTMLDivElement[]) => void;
  activeMatchIndex?: number;
  searchMatches?: string;
  // for tabs
  // tabs?: TabItem[];
  // setTabs?: React.Dispatch<React.SetStateAction<TabItem[]>>;
  // setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  // activeTab?: string;
  // add column
  scrollOnNextAdd?: boolean;
  setScrollOnNextAdd?: React.Dispatch<React.SetStateAction<boolean>>;
}

const DownloadReportTableSection: React.FC<DownloadReportTableSectionProps> = ({
  hasMore,
  columnTimeSettings,
  onTimeSettingsChange,
  headerList,
  shipmentData,
  // sheetTitle,
  // setSheetTitle,
  // onAddEmptyColumn,
  onDeleteCustomColumn,
  onCustomCellChange,
  onHeaderUpdate,
  handleLoadMore,
  loading,
  // calculationStatus,
  // handleRecalculate,
  // lastRefreshFormattedTime,

  searchQuery,
  onFoundMatches,
  activeMatchIndex,
  // for tabs
  // tabs,
  // setTabs,
  // setActiveTab,
  // activeTab,
  // add column
  scrollOnNextAdd,
  setScrollOnNextAdd,
}) => {
  const cellRefs = useRef<HTMLDivElement[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    cellRefs.current = [];
  }, [searchQuery, shipmentData]);

  const matchRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const allRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    Object.values(allRefs.current).forEach((cell) => {
      if (cell) {
        cell.style.backgroundColor = 'transparent';
        cell.style.fontWeight = '400';
      }
    });
    matchRefs.current = {};
    allRefs.current = {};
  }, [searchQuery, shipmentData]);

  useEffect(() => {
    Object.values(allRefs.current).forEach((cell) => {
      if (cell) {
        cell.style.backgroundColor = 'transparent';
        cell.style.fontWeight = '400';
      }
    });

    if (activeMatchIndex == null || activeMatchIndex < 0) return;

    const matches = Object.values(matchRefs.current).filter(
      Boolean
    ) as HTMLDivElement[];
    const target = matches[activeMatchIndex];
    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    target.style.backgroundColor = 'var(--surface-selected)';
    target.style.fontWeight = '600';
  }, [activeMatchIndex, searchQuery]);

  // useEffect(() => {
  //   const handle = requestAnimationFrame(() => {
  //     const validMatches = Object.values(matchRefs.current).filter(
  //       Boolean
  //     ) as HTMLDivElement[];
  //     if (onFoundMatches) {
  //       onFoundMatches(validMatches);
  //     }
  //   });

  //   return () => cancelAnimationFrame(handle);
  // }, [searchQuery, shipmentData, onFoundMatches])
  // ;

  useLayoutEffect(() => {
    const elements = Object.values(matchRefs.current).filter(Boolean);
    onFoundMatches?.(elements as HTMLDivElement[]);
  }, [searchQuery, shipmentData, onFoundMatches]);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [editingHeader, setEditingHeader] = useState<string | null>(null);
  const [headerEditValue, setHeaderEditValue] = useState<string>('');
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    key: string;
  } | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [bodyScrollY, setBodyScrollY] = useState(360);

  const measureBodyScrollY = useCallback(() => {
    const wrap = tableWrapperRef.current;
    if (!wrap || !shipmentData?.length) return;
    requestAnimationFrame(() => {
      const w = tableWrapperRef.current;
      if (!w) return;
      const thead = w.querySelector('.ant-table-thead') as HTMLElement | null;
      const theadH = thead?.offsetHeight ?? 0;
      if (theadH <= 0) return;
      const next = Math.floor(w.clientHeight - theadH);
      setBodyScrollY((prev) => {
        const clamped = Math.max(120, next);
        return clamped === prev ? prev : clamped;
      });
    });
  }, [shipmentData?.length]);

  const enabledHeaders = headerList.filter(
    (header): header is CompleteHeaderItem =>
      Boolean(header.enable) &&
      typeof header.key === 'string' &&
      typeof header.value === 'string'
  );

  const isDateColumn = (columnKey: string): boolean => {
    const dateKeys = [
      'created_at',
      'updated_at',
      'shipped_date',
      'scheduled_pickup_date',
      'actual_pickup_date',
      'scheduled_delivery_date',
      'transport_provider_eta',
      'estimated_delivery_date',
      'actual_delivery_date',
      'customer_preferred_delivery_date',
      'delivery_exception_date',
      'event_time',
    ];

    return dateKeys.includes(columnKey);

    // const key = columnKey.toLowerCase();
    // return (
    //   // key.includes('date') ||
    //   // key.includes('time') ||
    //   // key.includes('eta') ||
    //   // key.includes('delivery') ||
    //   // key.endsWith('at') ||
    //   // key.includes('utc') ||
    // );
  };

  const parseDate = (
    value: string | number | Date | null | undefined
  ): number => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    const parsed = parseShipmentDayjs(value);
    if (parsed) return parsed.valueOf();
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date.getTime();
    const stringValue = String(value);
    const customDate = new Date(
      stringValue.replace(/(\d+)(st|nd|rd|th)/, '$1')
    );
    return isNaN(customDate.getTime()) ? 0 : customDate.getTime();
  };

  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return shipmentData;

    return [...shipmentData].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortOrder === 'asc' ? -1 : 1;
      if (bVal == null) return sortOrder === 'asc' ? 1 : -1;

      if (isDateColumn(sortField)) {
        const aTime = parseDate(String(aVal));
        const bTime = parseDate(String(bVal));
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortOrder === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [shipmentData, sortField, sortOrder]);

  const handleSort = (columnKey: string) => {
    if (sortField === columnKey) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortOrder(null);
        setSortField(null);
      }
    } else {
      setSortField(columnKey);
      setSortOrder('asc');
    }
  };

  const handleHeaderEditStart = (header: CompleteHeaderItem) => {
    setEditingHeader(header.key);
    setHeaderEditValue(header.value);
  };

  const handleHeaderEditSave = (header: CompleteHeaderItem) => {
    if (headerEditValue.trim() && headerEditValue !== header.value) {
      const newKey = headerEditValue.toLowerCase().replace(/\s+/g, '_');
      if (onHeaderUpdate) {
        onHeaderUpdate(header.key, newKey, headerEditValue);
      }
    }
    setEditingHeader(null);
  };

  const handleHeaderKeyDown = (
    e: React.KeyboardEvent,
    header: CompleteHeaderItem
  ) => {
    if (e.key === 'Enter') {
      handleHeaderEditSave(header);
    } else if (e.key === 'Escape') {
      setEditingHeader(null);
    }
  };

  const parseTeams = (teamsStr: string): teams[] => {
    if (!teamsStr) return [];

    try {
      let jsonString = teamsStr.replace(/'/g, '"');

      // replace UUID('...') with just the string
      jsonString = jsonString.replace(/UUID\("?(.*?)"?\)/g, '"$1"');
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse teams:', e);
      return [];
    }
  };

  useEffect(() => {
    const scrollContainer = tableWrapperRef.current?.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null;
    if (!scrollContainer) return;
    const handleScroll = () => setOpenDropdownKey(null);
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [shipmentData?.length]);

  const columns = enabledHeaders.map((header) => {
    const isCustomColumn = Boolean(header.isCustom);

    const isDate = isDateColumn(header.key);
    const cfg = columnTimeSettings[header.key];
    const columnTimezone = cfg?.timezone ?? 'local_scan_time';
    const columnFormat = cfg?.format ?? '%Y-%d-%m %H:%M';
    const isSorted = sortField === header.key;

    const formatOptions = [
      { label: 'dd-mm-yyyy hh:mm', value: '%d-%m-%Y %H:%M' },
      { label: 'mm-dd-yy hh:mm', value: '%m-%d-%Y %H:%M' },
      { label: 'yyyy-dd-mm hh:mm', value: '%Y-%d-%m %H:%M' },
    ];
    const timezoneOptions: { label: string; value: TimezoneValue }[] = [
      { label: 'Local scan time', value: 'local_scan_time' },
      { label: 'Origin', value: 'origin' },
      { label: 'Destination', value: 'destination' },
      // { label: 'User time',       value: 'user_timezone' },
    ];

    const titleComponent = (
      <Flex
        align='center'
        justify='space-between'
        gap={4}
        className='hoverable-cell'
      >
        <Flex
          align='center'
          gap={4}
          className='report-table-header-title'
          onClick={(e) => {
            e.stopPropagation();
            handleHeaderEditStart(header);
          }}
        >
          {editingHeader === header.key ? (
            <Input
              className='custom-editable-input'
              autoFocus
              value={headerEditValue}
              onChange={(e) => setHeaderEditValue(e.target.value)}
              onBlur={() => handleHeaderEditSave(header)}
              onKeyDown={(e) => handleHeaderKeyDown(e, header)}
              onClick={(e) => e.stopPropagation()}
              size='small'
            />
          ) : (
            <Tooltip title={String(header.value ?? '')} placement='topLeft'>
              <span className='report-table-header-label'>{header.value}</span>
            </Tooltip>
          )}
        </Flex>

        {isDate && (
          <Popover
            // getPopupContainer={() => tableWrapperRef.current || document.body}
            // destroyTooltipOnHide
            arrow={false}
            trigger='hover'
            placement='bottomLeft'
            open={openDropdownKey === header.key}
            overlayClassName={`drt-popover drt-popover-${header.key}`}
            onOpenChange={(open) =>
              setOpenDropdownKey(open ? header.key : null)
            }
            content={
              <div className='report-time-settings-content'>
                <div>
                  <CustomTitle
                    className='report-time-settings-title'
                    text='Time format'
                    fontSize='14px'
                    lineHeight='16px'
                    fontWeight='600'
                    marginBottom='8px'
                  />
                  <CustomRadioGroup
                    value={columnFormat}
                    horizontal={false}
                    onChange={(e) =>
                      onTimeSettingsChange(header.key, {
                        format: e.target.value,
                      })
                    }
                    options={formatOptions}
                  />
                </div>

                <div>
                  <CustomTitle
                    className='report-time-settings-title'
                    text='Time zone'
                    fontSize='14px'
                    lineHeight='16px'
                    fontWeight='600'
                    marginBottom='8px'
                  />
                  <CustomRadioGroup
                    value={columnTimezone}
                    horizontal={false}
                    onChange={(e) =>
                      onTimeSettingsChange(header.key, {
                        timezone: e.target.value,
                      })
                    }
                    options={timezoneOptions}
                  />
                </div>
              </div>
            }
          >
            <span className='hoverable-btn'>
              <MapSettingsIcon color='var(--text-secondary)' size={16} />
            </span>
          </Popover>
        )}

        {isCustomColumn && (
          <span
            onClick={() => onDeleteCustomColumn(header.key)}
            className='height-16 cursor-pointer'
          >
            <DeleteIcon color='var(--text-secondary)' />
          </span>
        )}

        <Flex
          align='center'
          gap={0}
          className='hoverable-btn'
          onClick={() => handleSort(header.key)}
        >
          <SortUpIcons
            color={
              isSorted && sortOrder === 'asc'
                ? 'var(--text-primary)'
                : 'var(--text-secondary)'
            }
          />
          <span className='report-sort-down-icon'>
            <SortDownIcons
              color={
                isSorted && sortOrder === 'desc'
                  ? 'var(--text-primary)'
                  : 'var(--text-secondary)'
              }
            />
          </span>
        </Flex>
      </Flex>
    );

    // Narrower default widths so ~6 columns fit at typical desktop zoom (was 220/170 → ~4.5 cols).
    const baseColumn = {
      title: titleComponent,
      dataIndex: header.key,
      key: header.key,
      width: isCustomColumn ? 128 : 165,
    };

    if (isCustomColumn) {
      return {
        ...baseColumn,
        render: (value: unknown, _record: ShipmentItem, rowIndex: number) => {
          const isEditing =
            editingCell?.rowIndex === rowIndex &&
            editingCell?.key === header.key;

          if (isEditing) {
            return (
              <div className='download-report-editor-input add-download-note-active'>
                <Input
                  size='small'
                  autoFocus
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingCell(null);
                    onCustomCellChange(rowIndex, header.key, tempValue);
                  }}
                  onPressEnter={() => {
                    setEditingCell(null);
                    onCustomCellChange(rowIndex, header.key, tempValue);
                  }}
                  className='report-custom-column-cell-input'
                />
              </div>
            );
          }

          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setEditingCell({ rowIndex, key: header.key });
                setTempValue(String(value ?? ''));
              }}
              className='report-custom-column-cell-item'
            >
              <TruncateWithTooltip
                key={`${tooltipResetKey}-${rowIndex}-${header.key}`}
                text={String(value ?? '')}
                max={20}
                render={(displayText) => <span>{displayText || '—'}</span>}
              />
            </div>
          );
        },
      };
    }

    if (header?.key === 'teams') {
      return {
        ...baseColumn,
        render: (_: unknown, record: ShipmentItem) => {
          if (!record?.teams) {
            return null;
          }
          let teamsArray = parseTeams(record?.teams);
          teamsArray = teamsArray.filter(
            (x) => x.name.toLocaleLowerCase() !== 'default'
          );
          if (!teamsArray?.length) return null;

          const [firstTeam, ...remainingTeams] = teamsArray;
          const hasMoreTeams = remainingTeams.length > 0;
          const text = firstTeam?.name?.toString() || '';
          const match =
            searchQuery &&
            text.toLowerCase().includes(searchQuery.toLowerCase());

          return (
            <div
              ref={(el) => {
                const k = `${header.key}-${record?.reference_number}`;
                if (el) {
                  allRefs.current[k] = el;
                  matchRefs.current[k] = match ? el : null;
                } else {
                  allRefs.current[k] = null;
                  matchRefs.current[k] = null;
                }
              }}
              className='flex white-space-nowrap cell-highlight'
            >
              <TeamBadge
                className='mr-1'
                color={firstTeam?.color}
                text={firstTeam?.name}
                tooltipText={firstTeam?.name}
                truncateLength={15}
              />
              {hasMoreTeams && (
                <Tooltip
                  title={remainingTeams
                    .map((t) => t?.name)
                    .filter(Boolean)
                    .join(', ')}
                  placement='top'
                >
                  <div className='inline-block cursor-pointer'>
                    <TeamBadge text={`+${remainingTeams.length}`} />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      };
    }

    // External tracking link rendering (same truncation pattern as default columns)
    if (header?.key === 'external_share_path') {
      return {
        ...baseColumn,
        render: (value: unknown, record: ShipmentItem) => {
          const path = String(value || '').trim();
          if (!path) return null;
          const isAbsolute = /^https?:\/\//i.test(path);
          // Some legacy runtime theme payloads may still expose baseUrl.
          const cfg = theme as typeof theme & { baseUrl?: string };
          const base = cfg.shareUrl || cfg.baseUrl || '';
          const normalizedBase = String(base).replace(/\/$/, '');
          const normalizedPath = path.startsWith('/') ? path : `/${path}`;
          const href = isAbsolute ? path : `${normalizedBase}${normalizedPath}`;
          const text = href;
          const match =
            searchQuery &&
            text.toLowerCase().includes(searchQuery.toLowerCase());

          return (
            <div
              ref={(el) => {
                const k = `${header.key}-${record?.reference_number}`;
                if (el) {
                  allRefs.current[k] = el;
                  matchRefs.current[k] = match ? el : null;
                } else {
                  allRefs.current[k] = null;
                  matchRefs.current[k] = null;
                }
              }}
              className='cell-highlight'
            >
              <TruncateWithTooltip
                text={href}
                max={20}
                render={(displayText) => (
                  <a href={href} target='_blank' rel='noopener noreferrer'>
                    {displayText}
                  </a>
                )}
              />
            </div>
          );
        },
      };
    }

    // Handle latest_location field
    if (header?.key === 'latest_location') {
      return {
        ...baseColumn,
        render: (
          value:
            { latest_lat?: number; latest_long?: number } | null | undefined
        ) => {
          if (
            !value ||
            typeof value !== 'object' ||
            Object.keys(value).length === 0
          ) {
            return null;
          }
          const { latest_lat, latest_long } = value;
          return (
            <div className='flex items-center gap-1'>
              <span>
                Lat:{' '}
                {typeof latest_lat === 'number' && Number.isFinite(latest_lat)
                  ? latest_lat.toFixed(6)
                  : '--'}
              </span>

              <span>
                Long:{' '}
                {typeof latest_long === 'number' && Number.isFinite(latest_long)
                  ? latest_long.toFixed(6)
                  : '--'}
              </span>
            </div>
          );
        },
      };
    }
    return {
      ...baseColumn,
      render: (value: unknown, record: ShipmentItem) => {
        const text = value?.toString() || '';
        const match =
          searchQuery && text.toLowerCase().includes(searchQuery.toLowerCase());

        return (
          <div
            ref={(el) => {
              const k = `${header.key}-${record?.reference_number}`;
              if (el) {
                allRefs.current[k] = el;
                matchRefs.current[k] = match ? el : null;
              } else {
                allRefs.current[k] = null;
                matchRefs.current[k] = null;
              }
            }}
            className='cell-highlight'
          >
            <TruncateWithTooltip
              text={String(value ?? '')}
              max={20}
              render={(displayText) => <span>{displayText}</span>}
            />
          </div>
        );
      },
    };
  });
  // const tableBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tableBody = tableWrapperRef.current?.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null;
    if (!tableBody) return;

    const handleScroll = () => {
      if (!hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = tableBody;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        handleLoadMore();
      }
    };

    tableBody.addEventListener('scroll', handleScroll);

    return () => {
      tableBody.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, shipmentData?.length, handleLoadMore]);

  // When the fixed scroll viewport is taller than loaded rows, there is no scrollbar
  // and scroll-to-load-more never fires — prefetch until the body scrolls or pages end.
  useEffect(() => {
    if (!hasMore || loading || !shipmentData?.length) return;

    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const tableBody = tableWrapperRef.current?.querySelector(
        '.ant-table-body'
      ) as HTMLElement | null;
      if (!tableBody) return;
      const { scrollHeight, clientHeight } = tableBody;
      if (scrollHeight <= clientHeight + 10) {
        handleLoadMore();
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [hasMore, loading, shipmentData?.length, handleLoadMore]);

  useLayoutEffect(() => {
    measureBodyScrollY();
  }, [measureBodyScrollY, headerList, shipmentData?.length, loading]);

  useLayoutEffect(() => {
    if (!shipmentData?.length) return undefined;
    const wrap = tableWrapperRef.current;
    if (!wrap) return undefined;
    const ro = new ResizeObserver(() => {
      measureBodyScrollY();
    });
    ro.observe(wrap);
    return () => {
      ro.disconnect();
    };
  }, [shipmentData?.length, measureBodyScrollY]);

  const prevColsRef = useRef<number | null>(null);

  const getTableScroller = (
    wrapper: HTMLDivElement | null
  ): HTMLElement | null => {
    if (!wrapper) return null;
    return (
      (wrapper.querySelector('.ant-table-body') as HTMLElement) ||
      (wrapper.querySelector('.ant-table-container') as HTMLElement) ||
      wrapper
    );
  };

  const rafIdRef = useRef<number | null>(null);
  const [tooltipResetKey, setTooltipResetKey] = useState(0);
  useEffect(() => {
    const scroller =
      (tableWrapperRef.current?.querySelector(
        '.ant-table-body'
      ) as HTMLElement) ||
      (tableWrapperRef.current?.querySelector(
        '.ant-table-container'
      ) as HTMLElement);

    if (!scroller) return;

    let lastLeft = scroller.scrollLeft;

    const handleScroll = () => {
      const currentLeft = scroller.scrollLeft;
      if (currentLeft !== lastLeft) {
        lastLeft = currentLeft;
        setTooltipResetKey((prev) => prev + 1);
      }
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, []);
  // close any open dropdowns on scroll or window resize
  useEffect(() => {
    const scroller = getTableScroller(tableWrapperRef.current);
    if (!scroller) return;
    if (!openDropdownKey) {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      return;
    }

    let prevLeft = scroller.scrollLeft;

    const tick = () => {
      const currLeft = scroller.scrollLeft;
      if (currLeft !== prevLeft) {
        setOpenDropdownKey(null);
        return;
      }
      prevLeft = currLeft;
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    const onWindowResize = () => setOpenDropdownKey(null);
    window.addEventListener('resize', onWindowResize);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize', onWindowResize);
    };
  }, [openDropdownKey]);
  // scroll to end when new column is added ok
  useEffect(() => {
    const currentCount = headerList?.length;
    if (prevColsRef.current === null) {
      prevColsRef.current = currentCount;
      return;
    }

    const prev = prevColsRef.current;
    prevColsRef.current = currentCount;

    if (!scrollOnNextAdd) return;

    if (currentCount > prev) {
      const scroller = getTableScroller(tableWrapperRef.current);
      if (scroller) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scroller.scrollTo({
              left: scroller.scrollWidth,
              behavior: 'smooth',
            });
            setScrollOnNextAdd?.(false);
          });
        });
      } else {
        setScrollOnNextAdd?.(false);
      }
    }
  }, [headerList?.length, scrollOnNextAdd, setScrollOnNextAdd]);

  return (
    <>
      <Flex className='responsive-flex'>
        {shipmentData?.length === 0 ? (
          <div className='dwonload-report-editor-empty'>
            <TableSectionEmptyState
              hasActiveSearch={(searchQuery ?? '').trim().length > 0}
            />
          </div>
        ) : (
          <>
            <div
              ref={tableWrapperRef}
              className='settings-content-wrapper download-report-table-section report-table-shell ant-table-wrapper'
            >
              <Table
                columns={columns}
                dataSource={sortedData}
                pagination={false}
                className='settings-table download-table'
                scroll={{ x: 'max-content', y: bodyScrollY }}
                rowHoverable={false}
                loading={loading}
                rowKey={(_, index) => index?.toString() ?? '0'}
                sticky={{ offsetHeader: 0 }}
              />
            </div>
          </>
        )}
      </Flex>
    </>
  );
};

export default DownloadReportTableSection;
